import React, { Suspense, lazy } from 'react';
import { HashRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import './App.css';

// Layouts
const AuthLayout = lazy(() => import('./components/layouts/AuthLayout'));
const MainLayout = lazy(() => import('./components/layouts/MainLayout'));

// Auth Pages
const Login = lazy(() => import('./pages/auth/Login'));
const Onboarding = lazy(() => import('./pages/auth/Onboarding'));

// Main App Pages
const ProjectsOverview = lazy(() => import('./pages/dashboard/ProjectsOverview'));
const ModelDashboard = lazy(() => import('./pages/dashboard/ModelDashboard'));
const CDPSelector = lazy(() => import('./pages/cdp/CDPSelector'));
const CDPAuth = lazy(() => import('./pages/cdp/CDPAuth'));

// Vertical/Use Case Pages (Update imports/references)
const VerticalSelector = lazy(() => import('./pages/verticals/VerticalWizard')); // Renamed Wizard to Selector conceptually
const UseCaseSelector = lazy(() => import('./pages/usecases/UseCaseSelector')); // Added UseCaseSelector import
const DataMapping = lazy(() => import('./pages/mapping/DataMapping')); // Import for data mapping

// Model Config Pages
const ModelConfiguration = lazy(() => import('./pages/models/ModelConfiguration'));
const DeploymentConfig = lazy(() => import('./pages/deploy/DeploymentConfig'));

// Other pages...
const ModelMonitor = lazy(() => import('./pages/deploy/ModelMonitor'));
const Documentation = lazy(() => import('./pages/documentation/Documentation'));
const Settings = lazy(() => import('./pages/settings/Settings'));

// Auth Guard for protected routes
const AuthGuard = ({ children }) => {
  // This is a placeholder for actual auth logic
  const isAuthenticated = localStorage.getItem('authenticated') === 'true';
  return isAuthenticated ? children : <Navigate to="/login" />;
};

function App() {
  return (
    <Router>
      <Suspense fallback={<div className="flex items-center justify-center h-screen">Loading...</div>}>
        <Routes>
          {/* Auth Routes */}
          <Route path="/login" element={<AuthLayout><Login /></AuthLayout>} />
          <Route path="/onboarding" element={<AuthLayout><Onboarding /></AuthLayout>} />
          
          {/* Main App Routes within MainLayout */}
          <Route path="/" element={<AuthGuard><MainLayout /></AuthGuard>}>
            <Route index element={<ProjectsOverview />} />
            <Route path="models/:modelId" element={<ModelDashboard />} />
            
            {/* CDP Flow */}
            <Route path="cdp">
              <Route index element={<CDPSelector />} />
              <Route path="auth/:provider" element={<CDPAuth />} /> {/* Connects to Segment Stage */}
            </Route>
            
            {/* Vertical & Use Case Flow */}
            <Route path="verticals" element={<VerticalSelector />} /> {/* Step 2a */}
            <Route path="usecases" element={<UseCaseSelector />} />  {/* Step 2b */}
            <Route path="mapping" element={<DataMapping />} />      {/* Step 2c */}
            
            {/* Model Configuration Flow */}
            <Route path="model-config" element={<ModelConfiguration />} /> {/* Step 3a */}
            <Route path="deploy" element={<DeploymentConfig />} />         {/* Step 3b */}
            
            {/* Other main routes */}
            <Route path="monitor" element={<ModelMonitor />} />
            <Route path="documentation" element={<Documentation />} />
            <Route path="settings" element={<Settings />} />
          </Route>
          
          {/* Fallback route */}
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </Suspense>
    </Router>
  );
}

export default App;